import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { FuseLoadingService } from '@fuse/services/loading/loading.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import {
  FutureDeliveriesByTrip,
  OrdersListService,
} from 'app/modules/components/sales/order-list-components/order-list.service';
import { LabelSelectionImpactDialogData } from './label-selection-impact-dialog.types';

/**
 * Bloquea (o, sin impacto, confirma) un cambio de control logístico: si hay entregas futuras
 * pendientes, o no se pudo verificar, el diálogo es informativo y siempre cierra con `false`.
 *
 * Advertencia previa a un cambio de control logístico sobre un material ya guardado: explica qué
 * pasa en la APP de Field Sales y muestra CUÁLES entregas de salida futuras quedan afectadas,
 * agrupadas por viaje.
 *
 * Cierra con `true` para aplicar el cambio y con `false` (o al descartarse) para dejarlo como
 * estaba, así quien llama nunca necesita saber cómo se calculó el impacto.
 *
 * La consulta corre desde adentro del diálogo y no antes de abrirlo: el usuario ve la
 * explicación de inmediato y la lista se va llenando, en vez de mirar un control congelado
 * mientras corre una consulta de órdenes. Un 403 (un administrador de materiales sin el permiso
 * de listar órdenes), un error o un material sin código dejan `available: false`: no se pudo
 * verificar el impacto, así que el cambio también se bloquea.
 */
@Component({
  selector: 'app-label-selection-impact-dialog',
  standalone: true,
  imports: [
    DatePipe,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
  templateUrl: './label-selection-impact-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelSelectionImpactDialogComponent {
  protected readonly data = inject<LabelSelectionImpactDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<LabelSelectionImpactDialogComponent, boolean>>(MatDialogRef);
  private readonly orders = inject(OrdersListService);
  private readonly fuseLoading = inject(FuseLoadingService);

  protected readonly isLoading = signal(true);
  protected readonly lookup = signal<FutureDeliveriesByTrip>({
    // Mientras el spinner está arriba no se verificó nada, así que la semilla tampoco puede ser
    // una afirmación: con `available: false` no se renderiza ni la lista ni la línea de "no hay
    // entregas afectadas".
    available: false,
    groups: [],
    total: 0,
    truncated: false,
  });

  /** Si hay lista que renderizar (una consulta fallida muestra solo el texto). */
  protected readonly hasDeliveries = computed(() => this.lookup().total > 0);

  /** The lookup could not run (403, error, no material code), so the impact is unknown. */
  protected readonly isUnverified = computed(() => !this.isLoading() && !this.lookup().available);

  /** Pending deliveries, or an impact we cannot rule out, forbid the change. */
  protected readonly isBlocked = computed(
    () => !this.isLoading() && (this.isUnverified() || this.hasDeliveries())
  );

  constructor() {
    // Este diálogo tiene su propio spinner; el overlay global de AVE encima sería ruido.
    this.fuseLoading.disableLoading();
    this.orders
      .findFutureOutboundDeliveriesByMaterial(
        this.data.materialCode ?? '',
        this.data.companyMongoId ?? ''
      )
      .pipe(
        // The service already swallows HTTP errors; this keeps a stray one from leaving the
        // dialog loading forever instead of blocked.
        catchError(() =>
          of<FutureDeliveriesByTrip>({ available: false, groups: [], total: 0, truncated: false })
        ),
        finalize(() => {
          this.isLoading.set(false);
          this.fuseLoading.enableLoading();
        }),
        takeUntilDestroyed()
      )
      .subscribe((result) => this.lookup.set(result));
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }

  protected confirm(): void {
    // Defense in depth: the template hides the button, but a blocked change must never apply.
    if (this.isLoading() || this.isBlocked()) return;
    this.dialogRef.close(true);
  }
}
